import { useEffect, useRef, useState } from 'react';
import { useInView } from '../site/motion';
import { prefersReducedMotion } from '../hooks/useReducedMotion';

interface Tier {
  name: string;
  price: string;
  period: string;
  role: string;
  colour: string;
  perks: string;
}

const TIERS: readonly Tier[] = [
  {
    name: 'Member',
    price: '25 USDT',
    period: 'a month',
    role: 'Member',
    colour: '#6cc79a',
    perks: 'Alerts channel and the daily recap.',
  },
  {
    name: 'Pro',
    price: '75 USDT',
    period: 'a month',
    role: 'Pro',
    colour: '#e0b470',
    perks: 'Everything above, plus the live calls channel.',
  },
  {
    name: 'Lifetime',
    price: '500 USDT',
    period: 'once',
    role: 'Lifetime',
    colour: '#c98bd8',
    perks: 'Never expires.',
  },
];

const COINS = ['BTC', 'ETH', 'SOL', 'USDT', 'LTC'] as const;

export function Subs() {
  const [ref, seen] = useInView<HTMLDivElement>(0.15);
  const [runKey, setRunKey] = useState(0);
  const [stage, setStage] = useState(0);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    for (const t of timers.current) window.clearTimeout(t);
    timers.current = [];

    if (!seen) {
      setStage(0);
      return;
    }
    if (prefersReducedMotion()) {
      setStage(3);
      return;
    }

    setStage(0);
    [500, 1600, 2500].forEach((at, i) => {
      timers.current.push(window.setTimeout(() => setStage(i + 1), at));
    });

    return () => {
      for (const t of timers.current) window.clearTimeout(t);
      timers.current = [];
    };
  }, [seen, runKey]);

  return (
    <div className="mk-case" ref={ref}>
      <div className="mk-case-head">
        <h2 className="mk-case-title">Crypto subscriptions with automatic roles</h2>
        <p className="mk-case-line">
          Members pay in whichever coin they hold and the bot gives them the right role the moment it
          confirms on chain.
        </p>
      </div>

      <div className="mk-case-grid">
        <div className="mk-case-flow">
          <ul className="sb-tiers">
            {TIERS.map((tier) => (
              <li className="sb-tier" key={tier.name}>
                <span className="sb-tier-bar" style={{ background: tier.colour }} aria-hidden="true" />
                <div className="sb-tier-body">
                  <div className="sb-tier-top">
                    <span className="sb-tier-name">{tier.name}</span>
                    <span className="sb-tier-price">
                      {tier.price} <em>{tier.period}</em>
                    </span>
                  </div>
                  <span className="sb-tier-role" style={{ color: tier.colour }}>
                    grants @{tier.role}
                  </span>
                  <span className="sb-tier-perks">{tier.perks}</span>
                </div>
              </li>
            ))}
          </ul>

          <div className="sb-coins">
            <span className="sb-coins-label">Takes</span>
            {COINS.map((coin) => (
              <span className="sb-coin" key={coin}>
                {coin}
              </span>
            ))}
          </div>

          <p className="sb-note">
            Renewals are tracked per member, so an expired subscription loses its role automatically
            without anyone checking a spreadsheet.
          </p>

          <button
            className="mk-replay"
            onClick={() => {
              setStage(0);
              setRunKey((k) => k + 1);
            }}
          >
            Run it again
          </button>
        </div>

        <div className="mk-case-out">
          <div className="dc" data-shown="true">
            <div className="dc-head">
              <span className="dc-hash" aria-hidden="true">
                #
              </span>
              <span className="dc-channel">subscriptions</span>
            </div>

            <div className="dc-body sb-thread">
              <div className="sb-step" data-shown={stage >= 1 ? 'true' : 'false'}>
                <span className="sb-chain">
                  <span className="sb-chain-dot" aria-hidden="true" />
                  Watching for a payment to the deposit address
                </span>
              </div>

              <div className="sb-step" data-shown={stage >= 2 ? 'true' : 'false'}>
                <span className="sb-chain sb-chain-ok">
                  <span className="sb-chain-dot" aria-hidden="true" />
                  75 USDT received, 12 confirmations
                </span>
              </div>

              <div className="ai-msg" data-shown={stage >= 3 ? 'true' : 'false'}>
                <img className="dc-avatar" src="/avatars/yellow.png" alt="" width={40} height={40} />
                <div className="dc-msg-body">
                  <div className="dc-msg-head">
                    <span className="dc-author">steamed bot</span>
                    <span className="dc-tag">APP</span>
                    <span className="dc-time">Today at 21:47</span>
                  </div>
                  <div className="dc-embed" style={{ borderInlineStartColor: '#e0b470' }}>
                    <p className="dc-embed-title">Subscription active</p>
                    <p className="dc-embed-sub">Welcome in. Your role is live.</p>
                    <div className="dc-embed-grid">
                      <div className="dc-field">
                        <span className="dc-field-label">Paid</span>
                        <span className="dc-field-value">75 USDT</span>
                      </div>
                      <div className="dc-field">
                        <span className="dc-field-label">Tier</span>
                        <span className="dc-field-value">Pro</span>
                      </div>
                      <div className="dc-field">
                        <span className="dc-field-label">Renews</span>
                        <span className="dc-field-value">in 30 days</span>
                      </div>
                      <div className="dc-field">
                        <span className="dc-field-label">Transaction</span>
                        <span className="dc-field-value">0x8f3a…21d7</span>
                      </div>
                    </div>
                    <p className="dc-embed-foot">Role granted automatically</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="sb-roles" data-shown={stage >= 3 ? 'true' : 'false'}>
              <span className="sb-roles-label">User</span>
              <span className="sb-role-chip" style={{ borderColor: '#e0b470', color: '#e0b470' }}>
                <span className="sb-role-dot" style={{ background: '#e0b470' }} aria-hidden="true" />
                Pro
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
